// App.js
import React, { useState, useContext } from 'react';
import { ChakraProvider, Box, CSSReset } from '@chakra-ui/react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import Sidebar from './Sidebar';
import TopBar from './TopBar';
import Pulse from './Pulse';
import Settings from './Settings';
import Tasks from './Tasks';
import Login from './Login';
import { AuthProvider, AuthContext } from './AuthContext';
import PushSubscription from './components/PushSubscription';
import PushSubscriber from './PushSubscriber.tsx';
import { Capacitor } from '@capacitor/core';
import Unsubscribe from './Unsubscribe';
import CompleteJob from './CompleteJob.jsx';
import NotificationsRequiredBanner from './components/NotificationsRequiredBanner';
import BetaBanner from './components/BetaBanner';
import { OnboardingChecklistProvider } from './onboarding/OnboardingChecklistProvider';
import HelpFeedback from './HelpFeedback.jsx';
import ServiceRecoveryBanner from './components/ServiceRecoveryBanner';
import theme from './theme';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();
  if (loading) return <div>Loading...</div>;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
};

const App = () => {
  // Track sidebar collapse state here so the main content can react
  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleToggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <AuthProvider>
      <ChakraProvider theme={theme}>
        <CSSReset />
        <BrowserRouter>
          <OnboardingChecklistProvider>
            {!Capacitor.isNativePlatform() && <PushSubscriber />}
            <AuthContext.Consumer>
              {({ user }) => (
                <Box height="100%" minH="100vh" display="flex" flexDirection="column" overflowX="hidden">
                  <TopBar user={user} onToggleSidebar={handleToggleSidebar} />
                  <Box display="flex" flex="1">
                    {user && <Sidebar isCollapsed={isCollapsed} onToggle={handleToggleSidebar} />}
                    <Box flex="1" display="flex" flexDirection="column">
                      {user && (
                        <Box px={{ base: 4, md: 6 }} py={4}>
                          <Box maxW="900px" mx="auto">
                            <ServiceRecoveryBanner mb={3} />
                            <BetaBanner mb={3} />
                            <NotificationsRequiredBanner />
                          </Box>
                        </Box>
                      )}
                      <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/unsubscribe" element={<Unsubscribe />} />
                        <Route path="/complete" element={<CompleteJob />} />
                        <Route
                          path="/"
                          element={<Navigate to={user ? "/dashboard" : "/login"} replace />}
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <Pulse />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tasks"
                          element={
                            <ProtectedRoute>
                              <Tasks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <Settings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/push-subscription"
                          element={
                            <ProtectedRoute>
                              <PushSubscription />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help-feedback"
                          element={
                            <ProtectedRoute>
                              <HelpFeedback />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Box>
                  </Box>
                </Box>
              )}
            </AuthContext.Consumer>
          </OnboardingChecklistProvider>
        </BrowserRouter>
      </ChakraProvider>
    </AuthProvider>
  );
};

export default App;
